<script lang="ts" setup>
  import { UIContentWrap, UIDatepicker, UITextSmRegular } from '@gohighlevel/ghl-ui'
  import { computed, onMounted, PropType, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    DateFieldElementOptions,
    ICustomFieldsLinkageForm,
    IElement,
    IRecipient,
    MovableComponentEvents,
    RecipientEntityTypeEnum,
  } from '../../types'

  import { DEFAULT_DATE_FORMAT, ELEMENTS_META, PAGE_SECTIONS } from '../../const'
  import { TemplateTypeEnum } from '../../enums'
  import { convertToUTC, convertUtcToUNIX, generateCustomLinkItemPayload, getSignatureElementColorStyles, validateDate } from '../../utils'
  import { CustomFieldTriggerLink } from '../CustomSelectTrigger'
  import { MovableComponentWrapper } from '../MovableComponentWrapper'
  import { CommonTooltip } from '../Tooltip'

  const props = defineProps({
    element: {
      required: true,
      type: Object as PropType<IElement<DateFieldElementOptions>>,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    snapContainer: {
      type: String,
    },
    storeMap: {
      type: Object,
      required: true,
    },
    boundContainer: {
      type: String,
    },
    compositionMap: {
      type: Object,
      required: true,
    },
  })
  const enableTooltip = ref(true)
  const isActiveElement = computed(() => props.isActive)
  const builderStore = props.storeMap.useBuilderStore()
  const emits = defineEmits([MovableComponentEvents.ON_ACTIVE])
  const { updateStyles, updateOption } = props.compositionMap.useElementOption()
  const store = props.storeMap.useAppStore()
  const isContentLibraryTemplate = computed(() => {
    return store.document.type === TemplateTypeEnum.CONTENT_LIBRARY
  })

  const recipient = computed(() => {
    const recipients = store.document?.recipients || []
    return recipients.find(({ id }) => id === props.element?.component.options.recipient) || ({} as IRecipient)
  })
  const recipientName = computed(() => {
    const { firstName, lastName } = recipient.value
    return firstName ? `${firstName} ${lastName || ''}` : ''
  })

  const onDrag = (styles: any) => {
    updateStyles('position', styles, props.element?.id)
    updateOption('isGhost', true, props.element?.id)
  }
  const onDragUpdate = (value: boolean) => {
    enableTooltip.value = !value
  }
  const onScale = ({ scaleX, scaleY }: { scaleX: number; scaleY: number }) => {
    updateStyles('scale', { scaleX, scaleY }, props.element?.id)
  }

  const onResize = ({ height, width }: { height: number; width: number }) => {
    updateStyles('dimensions', { height, width }, props.element?.id)
  }

  const onActive = (e: any, isActive: boolean) => {
    emits(MovableComponentEvents.ON_ACTIVE, e, isActive)
  }

  function del(e: any) {
    e.stopPropagation()
    const elementId = props?.element?.id as string
    if (!elementId) return

    // Check if element is in header/footer section BEFORE deleting
    const section = builderStore.getElementSection(elementId)
    if (section && (section === PAGE_SECTIONS.HEADERS || section === PAGE_SECTIONS.FOOTERS)) {
      // Delete from section (this handles propagation across all pages)
      builderStore.deleteElementInSection(elementId, section)
    } else {
      // Delete from content area
      builderStore.deleteElement(elementId)
    }

    // Clear active element if it was the deleted element
    if (builderStore.activeElementId === elementId) {
      builderStore.updateActiveElement(null)
    }

    builderStore.syncRecipient()
    builderStore.syncCustomFieldsForLinkage()
  }

  onMounted(() => {
    builderStore.syncRecipient()
  })
  function clone(e: MouseEvent) {
    e.stopImmediatePropagation()
    builderStore.cloneElement(props?.element?.id as string)
  }

  const onChange = (value: string) => {
    updateOption(ELEMENTS_META.TEXT, convertToUTC(value), props.element?.id)
  }
  const styles = computed(() => getSignatureElementColorStyles(recipient.value))
  const dimensions = computed(() => {
    const dimensionsObj = props.element?.responsiveStyles.large.dimensions
    return (
      {
        height: dimensionsObj?.height + 'px',
        width: dimensionsObj?.width + 'px',
      } || { height: 'auto', width: 'auto' }
    )
  })
  const colorStyles = computed(() => {
    const dimensionsObj = dimensions.value

    return {
      ...styles.value,
      marginTop: '-1px',
      border: `1px solid ${styles.value.borderColor} !important`,
      'box-shadow': 'none !important',
      ...(dimensionsObj ? { height: `${dimensionsObj.height}`, width: `${dimensionsObj.width}` } : {}),
    }
  })
  const position = computed(() => {
    const pos = props.element?.responsiveStyles.large.position
    return pos || { top: 0, left: 0, bottom: 0 }
  })
  const scale = computed(() => {
    const scaleObj = props.element?.responsiveStyles.large.scale
    return scaleObj || { scaleX: 1, scaleY: 1 }
  })

  const align = computed(() => props.element?.responsiveStyles.large.align)

  const docVariableList = computed(() => store.document?.variables || [])

  const availableDocumentVariablesList = computed(() => {
    const docDefaultVariable = docVariableList.value.map(vb => vb.fieldKey)
    builderStore.getAllLinkedDocumentVariables().forEach(vb => {
      if (!docDefaultVariable.includes(vb.fieldKey)) {
        docDefaultVariable.push(vb.fieldKey)
      }
    })
    return docDefaultVariable || []
  })

  const isContactRecipient = computed(
    () => props.element?.component.options.recipient && props.element?.component.options.entityName === RecipientEntityTypeEnum.CONTACTS
  )

  /**
   * Custom-linkage / "linked fields" is not supported for fillables that live inside a
   * header or footer section — those sections are duplicated across every page and a
   * per-instance linkage doesn't make sense there. Hide the linkage trigger on the
   * overlay (mirrors the same gating in the property tray GroupDateField.vue).
   */
  const isElementInHeaderOrFooter = computed(() => {
    if (!props.element?.id || typeof builderStore.getElementSection !== 'function') return false
    const section = builderStore.getElementSection(props.element.id)
    return section === PAGE_SECTIONS.HEADERS || section === PAGE_SECTIONS.FOOTERS
  })

  const contactCustomValueMapping = computed(() => {
    return builderStore.getContactCustomValueLinkMapping()
  })

  const onSelectCustomValueLinkage = (formValue: ICustomFieldsLinkageForm) => {
    const payload = generateCustomLinkItemPayload(formValue)
    builderStore.updateCustomValueLinkage(formValue.elementId, formValue.elementType, payload)
  }
  const onDragEnd = (options: any) => {
    const ok = builderStore.moveFloatingElement(props.element?.id as string, options)
    if (ok === false) return
    onDrag({
      top: options.styles.top,
      left: options.styles.left,
      bottom: options.styles.bottom,
      right: options.styles.right,
      height: options.styles.height,
      width: options.styles.width,
      preferBottom: options.styles.preferBottom,
      preferRight: options.styles.preferRight,
    })
  }

  const i18nLocale = useI18n()
</script>

<template>
  <MovableComponentWrapper
    :key="`moveable-element-${props.element?.id}`"
    :id="props.element?.id as string"
    @on-drag="onDrag"
    @on-scale="onScale"
    @on-active="onActive"
    @on-resize="onResize"
    @on-drag-update="onDragUpdate"
    @on-drag-end="onDragEnd"
    :is-active-element="isActiveElement"
    :is-editable="builderStore.isDocumentEditable"
    :position="position"
    :scale="scale"
    :align="align"
    :dimensions="props.element?.responsiveStyles.large.dimensions"
    :is-ghost="props.element?.component.options.isGhost"
    :snap-container="props.snapContainer"
    :bound-container="props.boundContainer"
    :is-drag-element="props.element?.component.isDraggable"
    :resizable="true"
    :keep-ratio="false"
    :render-directions="['se']"
    :min-height="26"
    :max-height="26"
    :min-width="15"
    :color-override="styles.placeholderColor"
    :inactive-color-override="styles.borderColor"
    :with-double-click="false"
  >
    <template #component>
      <span v-if="element?.component.options.required" class="option--required">{{ $t('common.requiredIndicator') }}</span>
      <div class="el-wrapper date-field-el h-[inherit]">
        <UIContentWrap :locale="i18nLocale?.locale?.value || 'en-US'" class="p-0" style="display: contents">
          <UIDatepicker
            :class="{
              'hidden-suffix': props.element?.component.options.text,
            }"
            :key="props.element?.id + `_` + props.element?.component.options.dateFormat"
            :is-date-disabled="
              (timestamp: number) =>
                builderStore.isDocumentEditable ? validateDate(props.element?.component.options.availableDates, timestamp) : false
            "
            class="date-field rounded-sm !text-sm !cursor-pointer"
            :style="!props.element?.component.options.text ? colorStyles : ''"
            :id="`floating_date_field-${props.element?.id}`"
            :placeholder="props.element?.component.options.placeholder"
            type="date"
            @update:value="onChange"
            size="medium"
            :value="props.element?.component.options.text ? convertUtcToUNIX(props.element?.component.options.text) : null"
            :format="props.element?.component.options.dateFormat || DEFAULT_DATE_FORMAT"
            :actions="['now', 'clear']"
            :placement="'top-end'"
          />
        </UIContentWrap>
      </div>
    </template>
    <template #tooltip v-if="recipientName">
      <UITextSmRegular class="capitalize font-semibold py-1 px-4 text-xs" :style="{ color: styles.placeholderColor }">
        {{ recipientName }}
      </UITextSmRegular>
    </template>
    <template #popup-options>
      <CommonTooltip @on-clone="clone" @on-delete="del">
        <template #extra>
          <CustomFieldTriggerLink
            v-if="isActiveElement && builderStore.isDocumentEditable && isContactRecipient && !isElementInHeaderOrFooter"
            :element="props.element"
            :popover-id="'popover-filed-link-' + props.element?.id"
            :label="$t('common.linkResponseToCustomFields')"
            :available-document-variables="availableDocumentVariablesList"
            @custom-variable-added="onSelectCustomValueLinkage"
            :contact-custom-value-mapping="contactCustomValueMapping"
            :is-document-variable-disabled="isContentLibraryTemplate"
          />
        </template>
      </CommonTooltip>
    </template>
  </MovableComponentWrapper>
</template>

<style lang="scss">
  .date-field {
    .n-input {
      --n-color-focus: v-bind('styles.backgroundColor') !important;
      --n-height: v-bind('dimensions.height') !important;
      --n-border-hover: 1px solid transparent !important;
      --n-border-pressed: 1px solid transparent !important;
      --n-border-focus: 1px solid transparent !important;
      --n-caret-color: 1px solid transparent !important;
      --n-box-shadow-focus: 1px solid transparent !important;
      --n-loading-color: 1px solid transparent !important;

      --n-border: 1px solid transparent !important;
      --n-color: transparent !important;
      box-shadow: none !important;
      border-radius: 2px;
      .n-input-wrapper {
        padding: 0 !important;
        .n-input__suffix {
          // display: none !important;
          .n-base-icon {
            color: rgb(141, 141, 141) !important;
          }
        }
        .n-input__input-el {
          padding: 0px 2px !important;
          font-weight: 600 !important;
        }
        .n-input__placeholder {
          padding: 0px 2px !important;
          color: v-bind('styles.placeholderColor') !important;
        }
      }
    }
  }

  .option--required {
    position: absolute;
    right: -7px;
    top: -17px;
    color: red;
    font-size: 22px;
    font-weight: 700;
  }
  .date-field {
    .n-input__input {
      font-size: 14px !important;
    }
  }
</style>
